import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Lock, ArrowLeft, Plus, Trash2, Save, Dumbbell,
  ArrowUp, ArrowDown, CheckCircle2, Archive, RefreshCw, Eye, Printer,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import TrainingPlanView from '../components/TrainingPlanView';
import AdminClientProgress from '../components/AdminClientProgress';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import {
  adminTrainingUsers, adminTrainingPlans, adminTrainingPlanCreate,
  adminTrainingPlanUpdate, adminTrainingPlanStatus, adminTrainingPlanDelete,
  adminTrainingPlanGet, adminClientProfileUpdate,
} from '../services/api';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';

const LEVELS = ['kezdő', 'középhaladó', 'haladó'];
const SPLIT_TYPES = ['teljestest', 'U-L', 'PPL', 'egyéni'];

const ClientProfileRow = ({ adminKey, user, onSaved, onProgress }) => {
  const [level, setLevel] = useState(user.level || 'kezdő');
  const [splitType, setSplitType] = useState(user.split?.type || '');
  const [spw, setSpw] = useState(user.split?.sessionsPerWeek || '');
  const [note, setNote] = useState(user.split?.note || '');
  const [saving, setSaving] = useState(false);

  const save = async (overrideLevel) => {
    setSaving(true);
    const lvl = overrideLevel || level;
    try {
      await adminClientProfileUpdate(adminKey, user.user_id, {
        level: lvl,
        split: (splitType || spw || note) ? {
          type: splitType || null,
          sessionsPerWeek: spw ? parseInt(spw, 10) : null,
          note: note || null,
        } : null,
      });
      if (overrideLevel) setLevel(overrideLevel);
      toast.success(`${user.name || user.email}: szint és split mentve.`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A mentés nem sikerült.');
    } finally { setSaving(false); }
  };

  const sugg = user.level_suggestion;

  return (
    <div className="space-y-0">
    <div data-testid={`client-profile-${user.email}`} className={`grid grid-cols-2 md:grid-cols-[1.4fr_1fr_1fr_0.7fr_1.2fr_auto_auto] gap-2 items-center rounded-lg border border-gray-800 bg-gray-900/40 px-3 py-2 ${sugg ? 'rounded-b-none border-b-0' : ''}`}>
      <div className="min-w-0 col-span-2 md:col-span-1">
        <div className="text-sm font-semibold text-white truncate">{user.name || '—'}</div>
        <div className="text-[11px] text-gray-500 truncate">{user.email}</div>
      </div>
      <select data-testid={`client-level-${user.email}`} value={level} onChange={(e) => setLevel(e.target.value)}
        className="h-9 rounded-md border border-gray-700 bg-gray-900 px-2 text-sm text-white">
        {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
      </select>
      <select data-testid={`client-split-${user.email}`} value={splitType} onChange={(e) => setSplitType(e.target.value)}
        className="h-9 rounded-md border border-gray-700 bg-gray-900 px-2 text-sm text-white">
        <option value="">Split…</option>
        {SPLIT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <Input data-testid={`client-spw-${user.email}`} type="number" min="1" max="14" placeholder="hét/db" value={spw}
        onChange={(e) => setSpw(e.target.value)} className="h-9 bg-gray-900 border-gray-700 text-white text-sm" />
      <Input data-testid={`client-split-note-${user.email}`} placeholder="pl. alsó/felső/alsó/felső" value={note}
        onChange={(e) => setNote(e.target.value)} className="h-9 bg-gray-900 border-gray-700 text-white text-sm" />
      <Button data-testid={`client-profile-save-${user.email}`} size="sm" onClick={() => save()} disabled={saving}
        className="bg-gray-800 hover:bg-yellow-600 hover:text-gray-900 text-gray-200 h-9">
        {saving ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />}
      </Button>
      <Button data-testid={`client-progress-btn-${user.email}`} size="sm" variant="outline" onClick={() => onProgress(user)}
        className="relative border-gray-700 text-gray-300 hover:bg-gray-800 h-9">
        Haladás
        {user.unread_messages > 0 && (
          <span data-testid={`client-unread-badge-${user.email}`}
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full bg-yellow-500 text-gray-900 text-[10px] font-bold flex items-center justify-center px-1">
            {user.unread_messages}
          </span>
        )}
      </Button>
    </div>
    {sugg && (
      <div data-testid={`level-suggestion-${user.email}`}
        className="flex flex-wrap items-center justify-between gap-2 rounded-b-lg border border-t-0 border-amber-700/40 bg-amber-500/10 px-3 py-2">
        <div className="text-xs text-amber-200/90 min-w-0">
          <span className="font-bold text-amber-300">Szintváltás javasolt: {sugg.to}</span>
          <span className="text-amber-200/70"> — {sugg.reason}</span>
        </div>
        <Button data-testid={`level-suggestion-accept-${user.email}`} size="sm" variant="outline"
          onClick={() => save(sugg.to)} disabled={saving}
          className="border-amber-700/60 text-amber-300 hover:bg-amber-500/15 h-7 text-xs shrink-0">
          Elfogadás
        </Button>
      </div>
    )}
    </div>
  );
};

const META_FIELDS = [
  ['jelleg', 'Időszak jellege', 'pl. előkondicionálás'],
  ['weeks_range', 'Mikrociklus hetek', 'pl. 1–12'],
  ['training_days', 'Edzésnapok', 'pl. hétfő, szerda, péntek'],
  ['duration', 'Edzésidőtartam', 'pl. kb. 1 óra'],
  ['start_weight_kg', 'Kezdeti testsúly (kg)', 'pl. 80'],
  ['start_bodyfat_pct', 'Kezdeti testzsír %', 'pl. 25'],
  ['intensity_note', 'Intenzitás', 'pl. testsúly %-hoz viszonyítva'],
  ['goal', 'Edzés célja', 'pl. izomtömeg növelés és szálkásítás'],
  ['split', 'Program megosztása', 'pl. teljestestes edzés heti 3×'],
  ['methods', 'Edzésmódszerek', 'pl. progresszív terhelés'],
];

const EXTRA_FIELDS = [
  ['levezetes', 'Levezetés'],
  ['kardio', 'Kiegészítő kardió edzés'],
  ['regeneracio', 'Regenerálódást elősegítő eljárások'],
  ['taplalkozas_kcal', 'Napi kcal bevitel'],
  ['taplalkozas_makro', 'Makró tápanyagok aránya'],
  ['etkezesek', 'Étkezések száma'],
  ['etkezes_idopontok', 'Étkezési időpontok'],
  ['folyadek', 'Napi folyadékbevitel'],
  ['kiegeszitok', 'Táplálék-kiegészítés'],
];

const EX_COLS = [
  ['week', 'Hét', 'w-14'],
  ['muscle_group', 'Izomcsoport', 'w-32'],
  ['name', 'Gyakorlat neve', 'w-48'],
  ['sets', 'Sorozat', 'w-16'],
  ['reps', 'Ismétlés', 'w-16'],
  ['intensity_pct', '1RM %', 'w-20'],
  ['intensity_kg', 'Kg', 'w-16'],
  ['tempo', 'Tempó', 'w-20'],
  ['rest', 'Pihenő', 'w-20'],
  ['methods', 'Egyéb módszer', 'w-28'],
  ['note', 'Megjegyzés', 'w-36'],
];

const STATUS_HU = {
  draft: { label: 'Piszkozat', cls: 'bg-gray-600/20 text-gray-300' },
  active: { label: 'Aktív', cls: 'bg-emerald-500/15 text-emerald-300' },
  archived: { label: 'Archivált', cls: 'bg-amber-500/15 text-amber-300' },
};

const PlanEditor = ({ adminKey, plan: initialPlan, onBack, onChanged }) => {
  const [plan, setPlan] = useState(initialPlan);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [preview, setPreview] = useState(false);

  // Fetch the athlete's workout_logs once so the preview can render the
  // per-exercise target zones (Cél 70% / Csúcs 95%).
  useEffect(() => {
    if (!plan?.id) return;
    adminTrainingPlanGet(adminKey, plan.id)
      .then((d) => setLogs(d?.workout_logs || []))
      .catch(() => { /* non-fatal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan?.id]);

  const setMeta = (k) => (e) => setPlan((p) => ({ ...p, meta: { ...p.meta, [k]: e.target.value } }));
  const setExtra = (k) => (e) => setPlan((p) => ({ ...p, extras: { ...p.extras, [k]: e.target.value } }));
  const setEx = (i, k) => (e) => setPlan((p) => {
    const exercises = [...p.exercises];
    exercises[i] = { ...exercises[i], [k]: e.target.value };
    return { ...p, exercises };
  });
  const addRow = (afterIdx) => setPlan((p) => {
    const exercises = [...p.exercises];
    const base = afterIdx != null ? exercises[afterIdx] : null;
    exercises.splice((afterIdx ?? exercises.length - 1) + 1, 0, {
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      week: base?.week || '', muscle_group: base?.muscle_group || '', name: '', sets: '', reps: '',
      intensity_pct: '', intensity_kg: '', tempo: '', rest: '', methods: '', note: '',
    });
    return { ...p, exercises };
  });
  const delRow = (i) => setPlan((p) => ({ ...p, exercises: p.exercises.filter((_, j) => j !== i) }));
  const moveRow = (i, d) => setPlan((p) => {
    const exercises = [...p.exercises];
    const t = i + d;
    if (t < 0 || t >= exercises.length) return p;
    [exercises[i], exercises[t]] = [exercises[t], exercises[i]];
    return { ...p, exercises };
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminTrainingPlanUpdate(adminKey, plan.id, {
        title: plan.title, meta: plan.meta, exercises: plan.exercises, extras: plan.extras,
      });
      setPlan(res.plan);
      toast.success('Terv elmentve.');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A mentés nem sikerült.');
    } finally { setSaving(false); }
  };

  const setStatus = async (status) => {
    try {
      const res = await adminTrainingPlanStatus(adminKey, plan.id, status);
      setPlan(res.plan);
      toast.success(status === 'active' ? 'Terv aktiválva — a vendég mostantól látja a fiókjában!' : 'Státusz frissítve.');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Nem sikerült.');
    }
  };

  const handleDelete = async () => {
    try {
      await adminTrainingPlanDelete(adminKey, plan.id);
      toast.success('Terv törölve.');
      onChanged();
      onBack();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A törlés nem sikerült.');
    }
  };

  const st = STATUS_HU[plan.status] || STATUS_HU.draft;
  const inputCls = 'h-9 bg-gray-900 border-gray-700 text-white text-sm';

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="plan-editor-back" onClick={onBack} className="text-gray-400 hover:text-white"><ArrowLeft size={18} /></button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-bold truncate">{plan.user_name || plan.user_email}</span>
                <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                <span data-testid="plan-version-badge" className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">
                  v{plan.version || 1}
                </span>
              </div>
              <div className="text-xs text-gray-500 truncate">{plan.user_email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="plan-print-btn" variant="ghost" size="sm" onClick={() => window.open(`/admin/edzesterv/${plan.id}`, '_blank')} className="text-gray-300 hover:text-white">
              <Printer size={14} className="mr-1.5" /> Nyomtatás / PDF
            </Button>
            <Button data-testid="plan-preview-toggle" variant="ghost" size="sm" onClick={() => setPreview(!preview)} className="text-gray-300 hover:text-white">
              <Eye size={14} className="mr-1.5" /> {preview ? 'Szerkesztés' : 'Előnézet'}
            </Button>
            {plan.status !== 'active' ? (
              <Button data-testid="plan-activate-btn" size="sm" onClick={() => setStatus('active')} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold">
                <CheckCircle2 size={14} className="mr-1.5" /> Aktiválás
              </Button>
            ) : (
              <Button data-testid="plan-archive-btn" variant="outline" size="sm" onClick={() => setStatus('archived')} className="border-amber-700 text-amber-300 hover:bg-amber-950">
                <Archive size={14} className="mr-1.5" /> Archiválás
              </Button>
            )}
            <Button data-testid="plan-save-btn" size="sm" onClick={save} disabled={saving} className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
              {saving ? <Loader2 className="mr-1.5 animate-spin" size={14} /> : <Save size={14} className="mr-1.5" />} Mentés
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {preview ? (
          <div className="max-w-4xl mx-auto bg-white rounded-2xl p-6 md:p-8">
            <TrainingPlanView plan={plan} workoutLogs={logs} />
          </div>
        ) : (
          <div className="max-w-6xl mx-auto space-y-6">
            <div>
              <Label className="text-gray-400 text-xs uppercase tracking-widest">Terv címe</Label>
              <Input data-testid="plan-title-input" value={plan.title}
                onChange={(e) => setPlan((p) => ({ ...p, title: e.target.value }))}
                className="mt-1.5 bg-gray-900 border-gray-700 text-white max-w-xl" />
            </div>

            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-500 mb-3">Fejléc adatok</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {META_FIELDS.map(([k, label, ph]) => (
                  <div key={k}>
                    <Label className="text-gray-400 text-[10px] uppercase tracking-widest">{label}</Label>
                    <Input data-testid={`plan-meta-${k}`} value={plan.meta?.[k] || ''} onChange={setMeta(k)} placeholder={ph} className={`mt-1 ${inputCls}`} />
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-3">
                <div>
                  <Label className="text-gray-400 text-[10px] uppercase tracking-widest">Általános bemelegítés</Label>
                  <Textarea data-testid="plan-meta-warmup_general" value={plan.meta?.warmup_general || ''} onChange={setMeta('warmup_general')} rows={2} className="mt-1 bg-gray-900 border-gray-700 text-white text-sm" />
                </div>
                <div>
                  <Label className="text-gray-400 text-[10px] uppercase tracking-widest">Speciális bemelegítés</Label>
                  <Textarea data-testid="plan-meta-warmup_special" value={plan.meta?.warmup_special || ''} onChange={setMeta('warmup_special')} rows={2} className="mt-1 bg-gray-900 border-gray-700 text-white text-sm" />
                </div>
              </div>
            </section>

            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-500">Gyakorlatok táblázata</h3>
                <Button data-testid="plan-add-row-btn" variant="outline" size="sm" onClick={() => addRow(null)} className="border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800">
                  <Plus size={14} className="mr-1" /> Új sor
                </Button>
              </div>
              <div className="overflow-x-auto rounded-xl border border-gray-800">
                <table className="w-full min-w-[1050px] border-collapse">
                  <thead>
                    <tr className="bg-gray-900">
                      {EX_COLS.map(([k, label]) => (
                        <th key={k} className="px-1.5 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400 text-left border-b border-gray-800">{label}</th>
                      ))}
                      <th className="px-1.5 py-2 border-b border-gray-800 w-24" />
                    </tr>
                  </thead>
                  <tbody>
                    {plan.exercises.map((ex, i) => (
                      <tr key={ex.id || i} data-testid={`plan-ex-row-${i}`} className="border-b border-gray-800/60">
                        {EX_COLS.map(([k, , w]) => (
                          <td key={k} className={`px-1 py-1 ${w}`}>
                            <Input data-testid={`plan-ex-${i}-${k}`} value={ex[k] || ''} onChange={setEx(i, k)} className="h-8 bg-gray-900 border-gray-800 text-white text-xs px-2" />
                          </td>
                        ))}
                        <td className="px-1 py-1 whitespace-nowrap">
                          <button data-testid={`plan-ex-${i}-up`} onClick={() => moveRow(i, -1)} disabled={i === 0} className="p-1 text-gray-500 hover:text-white disabled:opacity-20"><ArrowUp size={13} /></button>
                          <button data-testid={`plan-ex-${i}-down`} onClick={() => moveRow(i, 1)} disabled={i === plan.exercises.length - 1} className="p-1 text-gray-500 hover:text-white disabled:opacity-20"><ArrowDown size={13} /></button>
                          <button data-testid={`plan-ex-${i}-add`} onClick={() => addRow(i)} title="Sor beszúrása alá" className="p-1 text-emerald-500 hover:text-emerald-300"><Plus size={13} /></button>
                          <button data-testid={`plan-ex-${i}-del`} onClick={() => delRow(i)} className="p-1 text-red-500 hover:text-red-300"><Trash2 size={13} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-gray-600 mt-2">
                Tipp: azonos izomcsoport-név esetén a vendég nézetében automatikusan összevonva jelenik meg (mint az Excelben).
                A „Hét" oszlopba írj pl. „A" vagy „B" értéket, ha váltott heti programot adsz — a vendég külön szekcióban látja az A és B hetet.
              </p>
            </section>

            <section>
              <h3 className="text-xs font-bold uppercase tracking-widest text-yellow-500 mb-3">Levezetés, kardió, táplálkozás</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {EXTRA_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-gray-400 text-[10px] uppercase tracking-widest">{label}</Label>
                    <Textarea data-testid={`plan-extra-${k}`} value={plan.extras?.[k] || ''} onChange={setExtra(k)} rows={2} className="mt-1 bg-gray-900 border-gray-700 text-white text-sm" />
                  </div>
                ))}
              </div>
            </section>

            <div className="pt-4 border-t border-gray-800">
              <Button data-testid="plan-delete-btn" variant="outline" size="sm" onClick={() => setShowDelete(true)} className="border-red-900 text-red-400 hover:bg-red-950">
                <Trash2 size={14} className="mr-1.5" /> Terv törlése
              </Button>
            </div>
          </div>
        )}
      </main>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent data-testid="plan-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Terv törlése</AlertDialogTitle>
            <AlertDialogDescription>„{plan.title}" végleges törlése. Ez nem visszavonható.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégse</AlertDialogCancel>
            <AlertDialogAction data-testid="plan-delete-confirm" onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Törlés</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const AdminTrainingPage = () => {
  useDocumentMeta({ title: 'Edzéstervek | Orosz Coaching Admin' });
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(ADMIN_KEY_STORAGE) || '');
  const [keyInput, setKeyInput] = useState('');
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [progressUser, setProgressUser] = useState(null);
  const [newUserId, setNewUserId] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async (key) => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([adminTrainingPlans(key), adminTrainingUsers(key)]);
      setPlans(p.plans || []);
      setUsers(u.users || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error('Érvénytelen admin kulcs.');
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setAdminKey('');
      } else toast.error('Betöltési hiba.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (adminKey) load(adminKey); }, [adminKey, load]);

  const handleCreate = async () => {
    if (!newUserId) { toast.error('Válassz vendéget.'); return; }
    setCreating(true);
    try {
      const res = await adminTrainingPlanCreate(adminKey, { user_id: newUserId, title: newTitle || 'Edzésterv' });
      toast.success('Terv létrehozva — szerkesztheted.');
      setNewUserId(''); setNewTitle('');
      await load(adminKey);
      setEditing(res.plan);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A létrehozás nem sikerült.');
    } finally { setCreating(false); }
  };

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6">
        <form
          data-testid="admin-training-login"
          onSubmit={(e) => { e.preventDefault(); if (keyInput.trim()) { localStorage.setItem(ADMIN_KEY_STORAGE, keyInput.trim()); setAdminKey(keyInput.trim()); } }}
          className="w-full max-w-md bg-gray-800/80 rounded-2xl p-8 border border-gray-700 shadow-2xl"
        >
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <Dumbbell className="text-yellow-500" size={28} />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-center mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>Edzéstervek</h1>
          <Input data-testid="admin-training-key" type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="ADMIN_API_KEY" className="bg-gray-900 border-gray-700 text-white mb-4" autoFocus />
          <Button data-testid="admin-training-login-btn" type="submit" disabled={!keyInput.trim()} className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold">
            <Lock className="mr-2" size={16} /> Belépés
          </Button>
        </form>
      </div>
    );
  }

  if (progressUser) {
    return (
      <AdminClientProgress
        adminKey={adminKey}
        user={progressUser}
        onBack={() => { setProgressUser(null); load(adminKey); }}
      />
    );
  }

  if (editing) {
    return (
      <PlanEditor
        adminKey={adminKey}
        plan={editing}
        onBack={() => setEditing(null)}
        onChanged={() => load(adminKey)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="admin-training-back" className="text-gray-400 hover:text-white"><ArrowLeft size={18} /></Link>
            <div className="w-10 h-10 rounded-full bg-yellow-600/20 flex items-center justify-center">
              <Dumbbell className="text-yellow-500" size={20} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Edzéstervek</h1>
              <p className="text-xs text-gray-400">Tervek készítése és aktiválása a vendégeknek</p>
            </div>
          </div>
          <Button data-testid="admin-training-refresh" variant="ghost" size="sm" onClick={() => load(adminKey)} className="text-gray-300 hover:text-white">
            <RefreshCw size={14} />
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-5xl space-y-8">
        <section className="rounded-2xl border border-gray-800 bg-gray-900/60 p-5">
          <h2 className="text-xs font-bold uppercase tracking-widest text-yellow-500 mb-3">Új edzésterv</h2>
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-gray-400 text-[10px] uppercase tracking-widest">Vendég</Label>
              <select
                data-testid="new-plan-user-select"
                value={newUserId}
                onChange={(e) => setNewUserId(e.target.value)}
                className="mt-1 h-10 w-full rounded-md border border-gray-700 bg-gray-900 px-3 text-sm text-white"
              >
                <option value="">Válassz vendéget…</option>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name ? `${u.name} — ` : ''}{u.email}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-gray-400 text-[10px] uppercase tracking-widest">Terv címe</Label>
              <Input data-testid="new-plan-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="pl. Kezdő edzésterve: 1-25 hét" className="mt-1 h-10 bg-gray-900 border-gray-700 text-white" />
            </div>
            <Button data-testid="new-plan-create-btn" onClick={handleCreate} disabled={creating} className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold h-10">
              {creating ? <Loader2 className="animate-spin" size={14} /> : <><Plus size={14} className="mr-1.5" /> Létrehozás</>}
            </Button>
          </div>
        </section>

        <section data-testid="client-profiles-section" className="rounded-2xl border border-gray-800 bg-gray-900/60 p-5">
          <h2 className="text-xs font-bold uppercase tracking-widest text-yellow-500 mb-1">Vendégek — szint és felosztás</h2>
          <p className="text-[11px] text-gray-500 mb-3">
            A szint dönti el, milyen visszajelzést kap a vendég: kezdő = ismétlés-progresszió, középhaladó/haladó = becsült 1RM (RPE-korrekcióval).
            A heti edzésszám az adherencia-visszajelzéshez kell.
          </p>
          <div className="space-y-2">
            {users.map((u) => (
              <ClientProfileRow key={u.user_id} adminKey={adminKey} user={u} onSaved={() => load(adminKey)} onProgress={setProgressUser} />
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Tervek ({plans.length})</h2>
          {loading ? (
            <div className="text-center py-16"><Loader2 className="animate-spin mx-auto text-yellow-500" size={26} /></div>
          ) : plans.length === 0 ? (
            <div className="text-center py-16 text-gray-500">Még nincs edzésterv — hozz létre egyet fent!</div>
          ) : (
            <div className="space-y-2">
              {plans.map((p) => {
                const st = STATUS_HU[p.status] || STATUS_HU.draft;
                return (
                  <button
                    key={p.id}
                    data-testid={`plan-list-item-${p.id}`}
                    onClick={() => setEditing(p)}
                    className="w-full text-left rounded-xl border border-gray-800 bg-gray-900/60 hover:border-yellow-700 px-4 py-3 transition-colors"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold">{p.title}</span>
                          <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {p.user_name ? `${p.user_name} · ` : ''}{p.user_email} · {(p.exercises || []).filter((e) => (e.name || '').trim()).length} gyakorlat
                        </div>
                      </div>
                      <span className="text-xs text-gray-600">
                        {p.updated_at ? new Date(p.updated_at).toLocaleDateString('hu-HU') : ''}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default AdminTrainingPage;
